import { labs, pdf, vitals } from "lobbie";

export const formatTableData = (
    title: string,
    trendView: vitals.IVitalsTrendsView | labs.ILabTrendsView,
) => {
    if (trendView.trendCols.length < 1 || trendView.trendCols.length < 1) return;

    const _headers = trendView.trendCols.map((header) => {
        return header.dateUnix;
    });

    const rows: pdf.IPDFTableRow[] = [];
    trendView.trendRows.forEach((row) => {
        const formattedRow: pdf.IPDFTableRow = { cells: [] };

        formattedRow.cells = [
            {
                entries: [
                    {
                        value: "labName" in row ? row.labName : row.vitalName,
                        units: "",
                    },
                ],
                isNameCell: true,
            },
            ..._headers.map((header): pdf.IPDFTableCell => {
                const results = row.results[header];
                const newCell: pdf.IPDFTableCell = { entries: [] };
                if (results) {
                    newCell.entries = results.map((result): pdf.IPDFTableEntry => {
                        return {
                            value: result.value ? result.value.toString() : "",
                            units: result.units,
                        };
                    });
                } else {
                    newCell.entries = [{ value: "", units: "" }];
                }

                return newCell;
            }),
        ];
        rows.push(formattedRow);
    });

    const headers = [
        "labName" in trendView.trendRows[0] ? "Test" : "Vital",
        ..._headers.map((header) => {
            return new Date(header).toLocaleDateString("en-US", {
                year: "numeric",
                month: "short",
                day: "numeric",
            });
        }),
    ];
    return { title, headers, rows };
};
export const sortVitals = (tableData: pdf.IPDFTable) => {
    const vitalsOrder = new Map(
        [
            "Blood Pressure",
            "Temperature",
            "Respiratory rate",
            "Height",
            "Weight",
            "BMI",
            "Pain Level",
            "Oxygen Saturation",
        ].map((vital, index) => [vital, index]),
    );

    tableData.rows.sort((a, b) => {
        const indexA = vitalsOrder.get(a.cells[0].entries[0].value) ?? Infinity;
        const indexB = vitalsOrder.get(b.cells[0].entries[0].value) ?? Infinity;
        return indexA - indexB;
    });
};
const findAndRemove = (rowName: string, table: pdf.IPDFTable) => {
    const rowIndex = table.rows.findIndex((row) => row.cells[0].entries[0].value === rowName);
    if (rowIndex !== -1) {
        return table.rows.splice(rowIndex, 1)[0];
    }
};
export const addBMIRow = (tableData: pdf.IPDFTable) => {
    const heightRow = tableData.rows.find((row) => row.cells[0].entries[0].value === "Height");
    const weightRow = tableData.rows.find((row) => row.cells[0].entries[0].value === "Weight");
    const newBMIRow: pdf.IPDFTableRow = {
        cells: [{ entries: [{ value: "BMI", units: "" }], isNameCell: true }],
    };
    heightRow?.cells.forEach((heightCell, cellIndex) => {
        const weightCell = weightRow?.cells[cellIndex];
        if (cellIndex === 0) return;
        const newBMICell: pdf.IPDFTableCell = { entries: [] };
        heightCell.entries.forEach((heightEntry, entryIndex) => {
            const height = Number(heightEntry.value);
            const weight = Number(weightCell?.entries[entryIndex].value);
            const newBMIEntry: pdf.IPDFTableEntry = { value: "", units: "" };

            if (height && weight) {
                newBMIEntry.value = ((weight / (height * height)) * 703).toFixed(2);
            }

            newBMICell.entries.push(newBMIEntry);
        });
        newBMIRow.cells.push(newBMICell);
    });
    tableData.rows.push(newBMIRow);
};
export const addHeightandBMIRow = (tableData: pdf.IPDFTable) => {
    addBMIRow(tableData);
    const heightRow = findAndRemove("Height", tableData);
    const newHeightRow: pdf.IPDFTableRow = {
        cells: [{ entries: [{ value: "Height", units: "" }], isNameCell: true }],
    };
    if (heightRow) {
        heightRow.cells.forEach((cell, cellIndex) => {
            if (cellIndex === 0) return;
            const newHeightCell: pdf.IPDFTableCell = { entries: [] };
            cell.entries.forEach((entry) => {
                const heightEntry: pdf.IPDFTableEntry = { value: "", units: "ft" };
                if (entry.value) {
                    const heightInches = Number(entry.value);
                    const feet = Math.floor(heightInches / 12);
                    const inches = heightInches % 12;
                    const inchesEntry = { value: inches.toString(), units: "in" };

                    heightEntry.value = feet.toString();
                    heightEntry.units = "ft";
                    heightEntry.neighbor = inchesEntry;
                }
                newHeightCell.entries.push(heightEntry);
            });
            newHeightRow.cells.push(newHeightCell);
        });
    }
    tableData.rows.push(newHeightRow);
};

export const addBloodPressureRow = (tableData: pdf.IPDFTable) => {
    const systolicBpRow = findAndRemove("Systolic Blood Pressure", tableData);
    const diastolicBpRow = findAndRemove("Diastolic Blood Pressure", tableData);
    const bloodPressureRow: pdf.IPDFTableRow = {
        cells: [{ entries: [{ value: "Blood Pressure", units: "" }], isNameCell: true }],
    };
    if (systolicBpRow && diastolicBpRow) {
        systolicBpRow.cells.forEach((cell, cellIndex) => {
            if (cellIndex === 0) return;
            const newCell: pdf.IPDFTableCell = { entries: [] };
            const diastolicBpCell = diastolicBpRow.cells[cellIndex];
            cell.entries.forEach((entry, entryIndex) => {
                const systolicValue = entry.value;
                const systolicUnits = "/";

                const diastolicValue = diastolicBpCell.entries[entryIndex].value;
                const diastolicUnits = diastolicBpCell.entries[entryIndex].units;

                const diastolicEntry = { value: diastolicValue, units: diastolicUnits };

                const newEntry = {
                    value: systolicValue,
                    units: systolicUnits,
                    neighbor: diastolicEntry,
                };

                newCell.entries.push(newEntry);
            });
            bloodPressureRow.cells.push(newCell);
        });

        tableData.rows.push(bloodPressureRow);
    }
};
